import type { PasswordStrength, LeetIntensity } from "./common";
import type { StaticTextKey, UiLanguageCode, UiTranslations } from "./i18n";

export type UiLanguageCode = "de" | "en";

export type StaticTextKey = "eyebrow" |
	"headline" |
	"subtitle" |
	"lengthLabel" |
	"directInputLabel" |
	"leetIntensityLabel" |
	"leetOptionOff" |
	"leetOptionMild" |
	"leetOptionNormal" |
	"leetOptionAggressive" |
	"useUmlautsTitle" |
	"useUmlautsSubtitle" |
	"useUnicodeTitle" |
	"useUnicodeSubtitle" |
	"generateButton" |
	"copyButton" |
	"optionsHint" |
	"strengthPending" |
	"entropyLabel" |
	"bitsLabel" |
	"crackTimeLabel";

export type UiTranslations = {
	documentTitle: string;
	staticTexts: Record<StaticTextKey, string>;
	passwordPlaceholder: string;
	crackTimeUnknown: string;
	strengthLabelsByScore: Record<PasswordStrength["score"], string>;
	strengthScoreText: (score: PasswordStrength["score"]) => string;
	nextLanguageButtonLabel: (nextLanguage: UiLanguageCode) => string;
	statusGenerating: string;
	statusValidInteger: string;
	statusGenerateFirst: string;
	statusCopied: string;
	statusCopyFailed: string;
	statusUnknownError: string;
	statusUnableToGenerate: string;
	leetLabels: Record<LeetIntensity, string>;
	toggleLabels: {
		on: string;
		off: string;
	};
	statusGenerated: (details: {
		length: number;
		leetIntensity: LeetIntensity;
		useUmlauts: boolean;
		useUnicodeSymbols: boolean;
	}) => string;
};

export const LANGUAGE_STORAGE_KEY = "spg-ui-language";

export const AVAILABLE_LANGUAGES: readonly UiLanguageCode[] = ["de", "en"];

export const UI_TRANSLATIONS: Record<UiLanguageCode, UiTranslations> = {
	de: {
		documentTitle: "Sicherer Passwort-Generator",
		staticTexts: {
			eyebrow: "Studio für lesbare Passwörter",
			headline: "Erzeuge sichere Passwörter, die man sich trotzdem merken kann.",
			subtitle: "Symbole, Kunstwörter und Ziffernblöcke werden zu einem starken Muster kombiniert.",
			lengthLabel: "Länge",
			directInputLabel: "Direkte Eingabe",
			leetIntensityLabel: "1337 Intensität",
			leetOptionOff: "Aus",
			leetOptionMild: "Leicht",
			leetOptionNormal: "Normal",
			leetOptionAggressive: "Aggressiv",
			useUmlautsTitle: "Umlaute verwenden",
			useUmlautsSubtitle: "Erweiterter Vokalpool mit ä, ö, ü",
			useUnicodeTitle: "Unicode-Symbole verwenden",
			useUnicodeSubtitle: "Ŧ, Ø, Æ, © und mehr",
			generateButton: "Passwort erzeugen",
			copyButton: "Kopieren",
			optionsHint: "Tipp: 1337, Umlaute und Unicode-Symbole erhöhen die Variation. Manche Legacy-Dienste lehnen Nicht-ASCII-Zeichen ab.",
			strengthPending: "Stärke ausstehend",
			entropyLabel: "Entropie",
			bitsLabel: "Bit",
			crackTimeLabel: "Geschätzte Knackzeit",
		},
		passwordPlaceholder: "Klicke auf Erzeugen, um ein Passwort zu erstellen.",
		crackTimeUnknown: "Unbekannt",
		strengthLabelsByScore: {
			0: "Sehr schwach",
			1: "Schwach",
			2: "Mittel",
			3: "Stark",
			4: "Sehr stark",
		},
		strengthScoreText: (score) => `Wertung ${score}/4`,
		nextLanguageButtonLabel: (nextLanguage) => `Sprache: ${nextLanguage.toUpperCase()}`,
		statusGenerating: "Erzeuge Passwort...",
		statusValidInteger: "Bitte gib eine gültige ganzzahlige Länge ein.",
		statusGenerateFirst: "Erzeuge zuerst ein Passwort.",
		statusCopied: "Passwort in die Zwischenablage kopiert.",
		statusCopyFailed: "Kopieren fehlgeschlagen. Zwischenablage-Berechtigung ist eventuell blockiert.",
		statusUnknownError: "Unbekannter Fehler",
		statusUnableToGenerate: "Passwort konnte nicht erzeugt werden",
		leetLabels: {
			off: "aus",
			mild: "leicht",
			normal: "normal",
			aggressive: "aggressiv",
		},
		toggleLabels: {
			on: "an",
			off: "aus",
		},
		statusGenerated: ({ length, leetIntensity, useUmlauts, useUnicodeSymbols }) => {
			const leetLabel = UI_TRANSLATIONS.de.leetLabels[leetIntensity];
			const umlautLabel = useUmlauts
				? UI_TRANSLATIONS.de.toggleLabels.on
				: UI_TRANSLATIONS.de.toggleLabels.off;
			const unicodeLabel = useUnicodeSymbols
				? UI_TRANSLATIONS.de.toggleLabels.on
				: UI_TRANSLATIONS.de.toggleLabels.off;

			return `Passwort mit ${length} Zeichen erzeugt (1337: ${leetLabel}, Umlaute: ${umlautLabel}, Unicode-Symbole: ${unicodeLabel}).`;
		},
	},
	en: {
		documentTitle: "Secure Password Generator",
		staticTexts: {
			eyebrow: "Readable Password Studio",
			headline: "Generate secure passwords that humans can still remember.",
			subtitle: "Symbols, synthetic words, and digit blocks are mixed into a strong pattern.",
			lengthLabel: "Length",
			directInputLabel: "Direct input",
			leetIntensityLabel: "1337 intensity",
			leetOptionOff: "Off",
			leetOptionMild: "Mild",
			leetOptionNormal: "Normal",
			leetOptionAggressive: "Aggressive",
			useUmlautsTitle: "Use umlauts",
			useUmlautsSubtitle: "Expanded vowel pool with ä, ö, ü",
			useUnicodeTitle: "Use Unicode symbols",
			useUnicodeSubtitle: "T, O, AE, C and more",
			generateButton: "Generate Password",
			copyButton: "Copy",
			optionsHint: "Tip: 1337, umlauts, and Unicode symbols increase variation. Some legacy services may reject non-ASCII characters.",
			strengthPending: "Strength pending",
			entropyLabel: "Entropy",
			bitsLabel: "bits",
			crackTimeLabel: "Estimated crack time",
		},
		passwordPlaceholder: "Click generate to create a password.",
		crackTimeUnknown: "Unknown",
		strengthLabelsByScore: {
			0: "Very weak",
			1: "Weak",
			2: "Fair",
			3: "Strong",
			4: "Very strong",
		},
		strengthScoreText: (score) => `Score ${score}/4`,
		nextLanguageButtonLabel: (nextLanguage) => `Language: ${nextLanguage.toUpperCase()}`,
		statusGenerating: "Generating...",
		statusValidInteger: "Please enter a valid integer length.",
		statusGenerateFirst: "Generate a password first.",
		statusCopied: "Password copied to clipboard.",
		statusCopyFailed: "Copy failed. Clipboard permission may be blocked.",
		statusUnknownError: "Unknown error",
		statusUnableToGenerate: "Unable to generate password",
		leetLabels: {
			off: "off",
			mild: "mild",
			normal: "normal",
			aggressive: "aggressive",
		},
		toggleLabels: {
			on: "on",
			off: "off",
		},
		statusGenerated: ({ length, leetIntensity, useUmlauts, useUnicodeSymbols }) => {
			const leetLabel = UI_TRANSLATIONS.en.leetLabels[leetIntensity];
			const umlautLabel = useUmlauts
				? UI_TRANSLATIONS.en.toggleLabels.on
				: UI_TRANSLATIONS.en.toggleLabels.off;
			const unicodeLabel = useUnicodeSymbols
				? UI_TRANSLATIONS.en.toggleLabels.on
				: UI_TRANSLATIONS.en.toggleLabels.off;

			return `Generated ${length}-character password (1337: ${leetLabel}, umlauts: ${umlautLabel}, unicode symbols: ${unicodeLabel}).`;
		},
	},
};

export function getCurrentTranslation(currentLanguage: UiLanguageCode): UiTranslations {
	return UI_TRANSLATIONS[currentLanguage];
}

export function isUiLanguageCode(value: string): value is UiLanguageCode {
	return AVAILABLE_LANGUAGES.includes(value as UiLanguageCode);
}

export function isStaticTextKey(value: string): value is StaticTextKey {
	return value in UI_TRANSLATIONS.de.staticTexts;
}

export function resolveInitialLanguage(): UiLanguageCode {
	try {
		const persistedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
		if (persistedLanguage !== null && isUiLanguageCode(persistedLanguage)) {
			return persistedLanguage;
		}
	} catch {
		// Ignore storage access errors and fall back to default language.
	}

	return "de";
}

export function getNextLanguage(current: UiLanguageCode): UiLanguageCode {
	const currentIndex = AVAILABLE_LANGUAGES.indexOf(current);
	const nextIndex = (currentIndex + 1) % AVAILABLE_LANGUAGES.length;
	const nextLanguage = AVAILABLE_LANGUAGES[nextIndex];
	if (nextLanguage === undefined) {
		return "de";
	}

	return nextLanguage;
}

export function applyStaticTranslations(currentLanguage: UiLanguageCode, translatableElements: NodeListOf<HTMLElement>): void {
	const translation = getCurrentTranslation(currentLanguage);

	for (const element of translatableElements) {
		const translationKey = element.dataset.i18n;
		if (translationKey === undefined || !isStaticTextKey(translationKey)) {
			continue;
		}

		element.textContent = translation.staticTexts[translationKey];
	}
}

